// frontend/src/Pages/AdvancePaymentsPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../apiClient";

const AdvancePaymentsPage = () => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole");

  // Auth guard: redirect non-admins
  useEffect(() => {
    if (userRole !== "admin") {
      navigate("/dashboard");
    }
  }, [userRole, navigate]);

  const [activeTab, setActiveTab] = useState('employee');
  const [employees, setEmployees] = useState([]);
  const [partners, setPartners] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Form states
  const [employeeId, setEmployeeId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [amountPLN, setAmountPLN] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState('');

  // Fetch data
  useEffect(() => {
    if (userRole !== "admin") return;

    const fetchData = async () => {
      try {
        const [empData, partData, advData] = await Promise.all([
          apiClient.get("/hr/employees"),
          apiClient.get("/hr/partners"),
          apiClient.get("/hr/advances")
        ]);
        setEmployees(empData);
        setPartners(partData);
        setAdvances(advData);
      } catch (err) {
        setMessage("❌ Failed to load data");
      }
    };
    fetchData();
  }, [userRole]);

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const amountGroszy = Math.round(parseFloat(amountPLN) * 100);
    if (isNaN(amountGroszy) || amountGroszy <= 0) {
      setMessage('⚠️ Please enter a valid amount');
      setLoading(false);
      return;
    }

    try {
      let payload;
      let url;

      if (activeTab === 'employee') {
        if (!employeeId) {
          setMessage('⚠️ Please select an employee');
          setLoading(false);
          return;
        }
        url = "/hr/advances/employee";
        payload = {
          employee_id: parseInt(employeeId),
          amount_groszy: amountGroszy,
          notes,
          date: date || new Date().toISOString().split('T')[0]
        };
      } else {
        if (!partnerId) {
          setMessage('⚠️ Please select a partner');
          setLoading(false);
          return;
        }
        url = "/hr/advances/partner";
        payload = {
          partner_id: parseInt(partnerId),
          amount_groszy: amountGroszy,
          notes,
          date: date || new Date().toISOString().split('T')[0]
        };
      }

      await apiClient.post(url, payload);

      // Refresh list
      const advData = await apiClient.get("/hr/advances");
      setAdvances(advData);
      setMessage(`✅ ${activeTab === 'employee' ? 'Employee' : 'Partner'} advance created!`);

      // Reset form
      setEmployeeId('');
      setPartnerId('');
      setAmountPLN('');
      setNotes('');
      setDate('');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to create advance"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ Are you sure you want to delete this advance?')) return;

    try {
      await apiClient.delete(`/hr/advances/${id}`);
      setAdvances(advances.filter(a => a.id !== id));
      setMessage('✅ Advance deleted');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to delete advance"}`);
    }
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? emp.full_name : `Employee ${id}`;
  };

  const getPartnerName = (id) => {
    const part = partners.find(p => p.id === id);
    return part ? part.full_name : `Partner ${id}`;
  };

  if (userRole !== "admin") return null;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto bg-[#F5E9D8] min-h-screen">
      <h1 className="text-2xl font-bold text-[#2D2F5E] mb-6">Advance Payments</h1>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${
          message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#2D2F5E] border-opacity-30 mb-4">
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'employee' 
              ? 'border-b-2 border-[#F76B4C] text-[#2D2F5E]' 
              : 'text-gray-500 hover:text-[#2D2F5E]'
          }`}
          onClick={() => setActiveTab('employee')}
        >
          Employee Advances
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'partner' 
              ? 'border-b-2 border-[#F76B4C] text-[#2D2F5E]' 
              : 'text-gray-500 hover:text-[#2D2F5E]'
          }`}
          onClick={() => setActiveTab('partner')}
        >
          Partner Advances
        </button>
      </div>

      {/* Form */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 mb-6">
        <h2 className="text-lg font-semibold text-[#2D2F5E] mb-3">
          Add {activeTab === 'employee' ? 'Employee' : 'Partner'} Advance
        </h2>
        <form onSubmit={handleCreateAdvance}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">
                {activeTab === 'employee' ? 'Employee' : 'Partner'}
              </label>
              <select
                value={activeTab === 'employee' ? employeeId : partnerId}
                onChange={(e) => activeTab === 'employee' ? setEmployeeId(e.target.value) : setPartnerId(e.target.value)}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                required
              >
                <option value="">Select...</option>
                {activeTab === 'employee'
                  ? employees.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.full_name} ({e.position})
                      </option>
                    ))
                  : partners.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.full_name} {p.designation ? `- ${p.designation}` : ''}
                      </option>
                    ))
                }
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">Amount (PLN)</label>
              <input
                type="number"
                step="0.01"
                value={amountPLN}
                onChange={(e) => setAmountPLN(e.target.value)}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                placeholder="e.g., 2500.50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">Notes</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                placeholder="Optional"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-[#F76B4C] hover:bg-[#E65A3C] text-white'
            }`}
          >
            {loading ? 'Saving...' : 'Add Advance'}
          </button>
        </form>
      </div>

      {/* Advances List */}
      <div className="bg-white rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 p-4">
        <h2 className="text-lg font-semibold text-[#2D2F5E] mb-3">All Advances</h2>
        {advances.length === 0 ? (
          <p className="text-[#2D2F5E] italic">No advances yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#2D2F5E] divide-opacity-20">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[#2D2F5E]">Type</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[#2D2F5E]">Name</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[#2D2F5E]">Amount (PLN)</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[#2D2F5E]">Date</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[#2D2F5E]">Notes</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[#2D2F5E]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2D2F5E] divide-opacity-10">
                {advances.map(adv => (
                  <tr key={adv.id} className="hover:bg-[#F5E9D8]">
                    <td className="px-4 py-2 text-[#2D2F5E]">{adv.employee_id ? 'Employee' : 'Partner'}</td>
                    <td className="px-4 py-2 text-[#2D2F5E]">
                      {adv.employee_id ? getEmployeeName(adv.employee_id) : getPartnerName(adv.partner_id)}
                    </td>
                    <td className="px-4 py-2 text-[#2D2F5E]">{(adv.amount_groszy / 100).toFixed(2)} PLN</td>
                    <td className="px-4 py-2 text-[#2D2F5E]">
                      {adv.date || new Date(adv.timestamp).toISOString().split('T')[0]}
                    </td>
                    <td className="px-4 py-2 text-[#2D2F5E] max-w-xs break-words">{adv.notes || '-'}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleDelete(adv.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancePaymentsPage;